import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {getImproveAPI} from "../gen";

/**
 * Query key constants for streak-related queries
 */
export const STREAK_QUERY_KEYS = {
	STREAK_INFO: "streakInfo"
} as const;

/**
 * Get current user's streak information
 * 
 * Returns:
 * - streak: Current streak count
 * - largestStreak: Highest streak ever achieved
 * - largestStreakDate: Date of largest streak
 * - streakDate: Last date streak was updated
 * - isStreakPaused: Whether streak is currently paused
 * 
 * @example
 * ```tsx
 * const { data: streakInfo, isLoading } = useGetStreakInfo();
 * 
 * if (isLoading) return <Spinner />;
 * return <div>Current Streak: {streakInfo.streak}</div>;
 * ```
 */
export const useGetStreakInfo = () => {
	const axiosConfig = useImproveAxios();
	const {userStreaksControllerGetStreakInfo} = getImproveAPI();

	return useQuery({
		queryKey: [STREAK_QUERY_KEYS.STREAK_INFO],
		queryFn: async () => {
			const response = await userStreaksControllerGetStreakInfo(axiosConfig);
			return response.data;
		},
		staleTime: 5 * 60 * 1000 // 5 minutes - streaks don't change super frequently
	});
};

/**
 * Update streak pause state (pause or unpause)
 * 
 * Automatically invalidates streak info cache after update.
 * 
 * @example
 * ```tsx
 * const pauseStreak = useUpdateStreakState();
 * 
 * const handlePause = () => {
 *   pauseStreak.mutate({ isStreakPaused: true }, {
 *     onSuccess: () => {
 *       notify.success('Streak paused successfully');
 *     }
 *   });
 * };
 * ```
 */
export const useUpdateStreakState = () => {
	const axiosConfig = useImproveAxios();
	const {userStreaksControllerUpdateStreakState} = getImproveAPI();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: {
			isStreakPaused: boolean;
			incrementStreakIfEligible?: boolean;
		}) => {
			const response = await userStreaksControllerUpdateStreakState(data, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [STREAK_QUERY_KEYS.STREAK_INFO]});
		}
	});
};

